<template>
  <div class="bg-white p-4">
    <div class="mb-4 flex items-center justify-between">
      <h3 class="text-lg font-semibold text-gray-800">Resultados da Análise</h3>
      <button @click="$emit('clear')" class="text-sm text-red-600 hover:text-red-800">Limpar</button>
    </div>

    <!-- Estatísticas Gerais -->
    <div class="mb-6 grid grid-cols-1 gap-4 md:grid-cols-3">
      <div class="bg-slate-50 p-4 shadow">
        <div class="text-xl font-bold text-boxdark">{{ cycleCount }}</div>
        <div class="text-sm text-gray-600">Ciclos Detectados</div>
      </div>

      <div class="bg-slate-50 p-4 shadow">
        <div class="text-xl font-bold text-boxdark">{{ formatMinutesToHoursWithReadbleMinutes(totalTime) }}</div>
        <div class="text-sm text-gray-600">Tempo Total</div>
      </div>

      <div class="bg-slate-50 p-4 shadow">
        <div class="text-xl font-bold text-boxdark">{{ formatMinutesToHoursWithReadbleMinutes(averageTime) }}</div>
        <div class="text-sm text-gray-600">Tempo Médio</div>
      </div>
    </div>

    <!-- Lista de Períodos -->
    <div v-if="periods.length > 0" class="space-y-3">
      <h4 class="font-medium text-gray-700">Períodos Detectados:</h4>

      <div class="max-h-64 space-y-2 overflow-y-auto">
        <div
          v-for="(period, index) in periods"
          :key="index"
          class="flex cursor-pointer items-center justify-between bg-gray-50 p-3 hover:bg-gray-100"
          @click="selectPeriod(period)">
          <div class="flex-1">
            <div class="text-sm font-medium text-gray-800">Ciclo {{ index + 1 }}</div>
            <div class="text-xs text-gray-600">{{ formatDateTime(period.start) }} - {{ formatDateTime(period.end) }}</div>
          </div>
          <div class="text-right">
            <div class="text-sm font-semibold text-blue-600">{{ period.duration.toFixed(1) }} min</div>
          </div>
        </div>
      </div>
    </div>

    <div v-else class="py-8 text-center text-gray-500">
      <div class="mb-2 text-lg">Nenhum ciclo detectado</div>
      <div class="text-sm">Ajuste os parâmetros e execute a análise novamente</div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { CycleAnalysisResult } from '@/services/cycle-analysis.service'
import { formatMinutesToHoursWithReadbleMinutes } from '@/utils/date/date-time'
import { computed } from 'vue'

interface Props {
  result: CycleAnalysisResult | null
}

interface Emits {
  (e: 'clear'): void
  (e: 'export'): void
  (e: 'select-period', period: any): void
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

// Computed properties
const cycleCount = computed(() => {
  const count = props.result?.cycle_count || 0
  return count
})

const totalTime = computed(() => {
  const time = props.result?.total_time || 0
  return time
})

const averageTime = computed(() => {
  const avg = props.result?.average_time || 0
  return avg
})

const periods = computed(() => {
  const periods = props.result?.periods || []
  return periods
})

// Funções
const formatDateTime = (dateString: string) => {
  const date = new Date(dateString)
  return date.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

const selectPeriod = (period: any) => {
  emit('select-period', period)
}
</script>
